/**
 * TimeModal — full-screen overlay hosting TimeWidget in expanded mode.
 *
 * Mirrors the CalendarModal pattern (overlay + ESC + backdrop click +
 * close button). Triggered from the home hero Time chip so the world
 * clock / stopwatch / timer stay accessible without polluting the feed
 * with a redundant card.
 */

import type { AweniContext } from '../../types';
import { TimeWidget } from './widgets/TimeWidget';
import type { WidgetContext } from './types';

const STYLES_ID = 'aweni-time-modal-styles';

function injectStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = STYLES_ID;
  style.textContent = `
    .aweni-tm-overlay {
      position: fixed; inset: 0;
      background: rgba(10,10,10,0.45);
      backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
      z-index: 5000;
      display: flex; align-items: flex-start; justify-content: center;
      padding: 32px 16px 16px;
      overflow-y: auto;
      animation: aweni-tm-fade-in 180ms ease;
    }
    @keyframes aweni-tm-fade-in { from { opacity: 0 } to { opacity: 1 } }
    .aweni-tm-card {
      background: var(--ln-bg-card, var(--card-bg, #fff));
      color: var(--ln-text-primary, var(--text-primary, #0A0A0A));
      border-radius: 20px;
      box-shadow: 0 24px 64px rgba(0,0,0,0.18);
      width: 100%;
      max-width: 520px;
      max-height: calc(100vh - 64px);
      overflow-y: auto;
      padding: 16px;
      position: relative;
    }
    .aweni-tm-close {
      position: absolute; top: 10px; inset-inline-end: 10px;
      width: 32px; height: 32px;
      border: none; background: var(--ln-bg-secondary, var(--bg-secondary, rgba(0,0,0,0.04)));
      border-radius: 50%; cursor: pointer; font-size: 16px; line-height: 1;
      color: inherit;
    }
    .aweni-tm-close:hover { background: var(--ln-bg-secondary-hover, rgba(0,0,0,0.08)); }
    .aweni-tm-title {
      font-size: 13px; font-weight: 700;
      color: var(--ln-text-secondary, var(--text-secondary, #6b6657));
      letter-spacing: 0.04em; text-transform: uppercase;
      margin: 0 36px 12px 4px;
    }
  `;
  document.head.appendChild(style);
}

export class TimeModal {
  private overlay: HTMLElement | null = null;
  private escHandler: ((e: KeyboardEvent) => void) | null = null;
  private ctx: AweniContext;

  constructor(ctx: AweniContext) {
    this.ctx = ctx;
  }

  show(): void {
    if (this.overlay) return;
    injectStyles();

    const isRtl = this.ctx.i18n.dir === 'rtl';
    const overlay = document.createElement('div');
    overlay.className = 'aweni-tm-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.close();
    });
    document.body.appendChild(overlay);
    this.overlay = overlay;

    const card = document.createElement('div');
    card.className = 'aweni-tm-card';

    const title = document.createElement('div');
    title.className = 'aweni-tm-title';
    title.textContent = this.ctx.i18n.t('time_modal_title') || this.ctx.i18n.t('door_time') || 'Time';
    card.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'aweni-tm-close';
    closeBtn.setAttribute('aria-label', this.ctx.i18n.t('common_close') || 'Close');
    closeBtn.innerHTML = '✕';
    closeBtn.addEventListener('click', () => this.close());
    card.appendChild(closeBtn);

    // TimeWidget expects a WidgetContext, not an AweniContext. Build a thin
    // adapter that exposes only what TimeWidget reads (t, locale, isRtl).
    const widgetCtx: WidgetContext = {
      t: this.ctx.i18n.t.bind(this.ctx.i18n),
      locale: this.ctx.i18n.locale,
      isRtl,
      isDark: false,
      userName: this.ctx.auth.user?.name || '',
      isGuest: !!this.ctx.auth.user?.is_guest,
      navigate: this.ctx.navigate.bind(this.ctx),
      showNotification: this.ctx.showNotification.bind(this.ctx),
      // Hero's TimeWidget host doesn't need location/weather; provide stubs.
      getLocationService: () => ({
        lat: null, lng: null, city: null, countryCode: null,
        lastUpdated: null, traveling: false,
      }),
      refreshLocation: async () => { /* noop */ },
      getWeatherData: () => null,
    };

    const slot = document.createElement('div');
    card.appendChild(slot);
    overlay.appendChild(card);

    try {
      TimeWidget.render(slot, 'expanded', widgetCtx);
    } catch (err) {
      slot.innerHTML = `<div style="padding:24px;text-align:center;color:var(--ln-text-secondary, var(--text-secondary));">${
        this.ctx.i18n.t('time_widget_error') || 'Time widget unavailable'
      }</div>`;
      console.warn('[TimeModal] TimeWidget render failed:', err);
    }

    this.escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this.escHandler);
  }

  close(): void {
    if (!this.overlay) return;
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
    this.overlay.remove();
    this.overlay = null;
  }
}
